import React from 'react';
import styled, {css} from 'styled-components';

import {Text} from '@famly/mf_data-display_text';
import {Stack} from '@famly/mf_layout_stack';
import {Box} from '@famly/mf_layout_box';
import {Icon} from '@famly/mf_data-display_icon';
import {Tooltip as MfTooltip} from '@famly/mf_data-display_tooltip';
import Tooltip from 'web-app/react/components/tooltip/tooltip';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';

import type * as Types from '../types';
import {SummaryPeriodItem} from './summary-period-item';

type Props = Types.SummaryPeriod & {
    currency: string;
    isPastSummary?: boolean;
    needsSquigglyLine?: boolean;
    index: number;
};

export const SummaryPeriod = ({
    index: summaryPeriodIndex,
    title,
    tuitions,
    fees,
    currency,
    discounts,
    totalsByPricingGroup,
    hasChildThatMovesPricingGroups,
    billingInfoByPricingGroup,
    tooltip,
    isPastSummary = false,
    needsSquigglyLine = false,
}: Props) => {
    const shouldCollapseSummaryPeriod = tuitions.length === 1 && fees.length === 0 && discounts.length === 0;
    const summaryPeriodItemElements = React.useMemo(
        () =>
            shouldCollapseSummaryPeriod ? (
                <SummaryPeriodItem
                    key={`tuition-0`}
                    {...tuitions[0]}
                    billingInfoByPricingGroup={billingInfoByPricingGroup}
                    currency={currency}
                    extraHigh={true}
                    emphasizePrice={!isPastSummary}
                    testId={`summary-period-${summaryPeriodIndex}-tuition-0`}
                />
            ) : (
                <>
                    {tuitions.map((tuition, index) => {
                        return (
                            <SummaryPeriodItem
                                key={`tuition-${index}`}
                                {...tuition}
                                billingInfoByPricingGroup={billingInfoByPricingGroup}
                                currency={currency}
                                emphasizePrice={!isPastSummary}
                                testId={`summary-period-${summaryPeriodIndex}-tuition-${index}`}
                            />
                        );
                    })}

                    {fees.map((fee, index) => {
                        return (
                            <SummaryPeriodItem
                                key={`fee-${index}`}
                                {...fee}
                                billingInfoByPricingGroup={billingInfoByPricingGroup}
                                currency={currency}
                                emphasizePrice={!isPastSummary}
                                testId={`summary-period-${summaryPeriodIndex}-fee-${index}`}
                            />
                        );
                    })}

                    {discounts.map((discount, index) => {
                        return (
                            <SummaryPeriodItem
                                key={`discount-${index}`}
                                {...discount}
                                billingInfoByPricingGroup={billingInfoByPricingGroup}
                                currency={currency}
                                isDiscount
                                emphasizePrice={!isPastSummary}
                                testId={`summary-period-${summaryPeriodIndex}-discount-${index}`}
                            />
                        );
                    })}

                    <SummaryPeriodItem
                        name={needsTranslations('SimplifiedUsBilling', 'Total')}
                        pricingGroups={totalsByPricingGroup}
                        billingInfoByPricingGroup={billingInfoByPricingGroup}
                        currency={currency}
                        emphasizePrice={!isPastSummary}
                        testId={`summary-period-${summaryPeriodIndex}-total`}
                    />
                </>
            ),
        [
            shouldCollapseSummaryPeriod,
            tuitions,
            billingInfoByPricingGroup,
            currency,
            isPastSummary,
            fees,
            discounts,
            totalsByPricingGroup,
            summaryPeriodIndex,
        ],
    );

    const isAlreadyBilled = React.useMemo(
        () => billingInfoByPricingGroup.some(billingInfo => billingInfo.isAlreadyBilled),
        [billingInfoByPricingGroup],
    );

    const color = React.useMemo(() => (isPastSummary ? 'n300' : undefined), [isPastSummary]);

    const alreadyBilledOverlay = React.useMemo(
        () => (
            <Box>
                <Stack direction="column">
                    <Text variant="body-small">
                        <p>{needsTranslations('SimplifiedUsBilling', 'This period has already been billed.')}</p>
                        <p>{needsTranslations('SimplifiedUsBilling', 'To make changes')}</p>
                        <Ul>
                            <li>{needsTranslations('SimplifiedUsBilling', 'Cancel the invoice')}</li>
                            <li>{needsTranslations('SimplifiedUsBilling', 'Edit the plan')}</li>
                            <li>{needsTranslations('SimplifiedUsBilling', 'Send a new invoice')}</li>
                        </Ul>
                    </Text>
                </Stack>
            </Box>
        ),
        [],
    );

    return (
        <TBODY
            data-e2e-class="summary-period"
            data-e2e-id={`summary-period-${summaryPeriodIndex}`}
            isDoubleLine={needsSquigglyLine}
        >
            <tr>
                <TD>
                    <Stack alignItems="center" spacing={2} py={2} marginTop={shouldCollapseSummaryPeriod ? 1 : 0}>
                        <Icon name="calendar_today" color={color} />
                        <Text emphasized variant="body-small" color={color} data-e2e-class="summary-period-title">
                            {title}
                            {hasChildThatMovesPricingGroups ? ' *' : null}
                        </Text>
                    </Stack>
                </TD>
                {isAlreadyBilled ? (
                    <TD rightAlign>
                        <Stack alignItems="center" spacing={1} py={2} direction="row" justifyContent="end">
                            <Tooltip trigger="hover" placement="top" overlay={alreadyBilledOverlay}>
                                <Stack>
                                    <Icon name="info" color={color} size={16} />
                                </Stack>
                            </Tooltip>
                            <Text variant="body-small" width="fit-content" color={color}>
                                {needsTranslations('SimplifiedUsBilling', 'Already billed')}
                            </Text>
                        </Stack>
                    </TD>
                ) : null}
                {tooltip ? (
                    <TD rightAlign>
                        <Stack alignItems="center" spacing={1} py={2} direction="row" justifyContent="end">
                            <MfTooltip
                                data-e2e-id="charged-as-purchase-tooltip"
                                placement="top"
                                tooltipContent={<Text variant="body-small">{tooltip.content}</Text>}
                            >
                                <Stack alignItems="center" spacing={2}>
                                    <Icon name="help" color="n300" size={18} />
                                    <Text color="n300" variant="body-small">
                                        {tooltip.title}
                                    </Text>
                                </Stack>
                            </MfTooltip>
                        </Stack>
                    </TD>
                ) : null}
            </tr>
            {summaryPeriodItemElements}
        </TBODY>
    );
};

const TBODY = styled.tbody<{isDoubleLine: boolean}>`
    margin-top: ${({theme}) => theme.mf.spacing(4)};
    margin-bottom: ${({theme}) => theme.mf.spacing(4)};
    border: none;
    background-color: transparent;
    border-bottom: ${({isDoubleLine}) => (isDoubleLine ? '3px double' : '1px solid')}
        ${({theme}) => theme.mf.colorPalette.n100};
`;

const TD = styled.td<{rightAlign?: boolean}>`
    padding: 0;
    cursor: default;
    ${({rightAlign}) =>
        rightAlign
            ? css`
                  text-align: right;
              `
            : ''};
`;

const Ul = styled.ol`
    list-style-type: disc;
`;
